import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Mail, LayoutDashboard, User, LogOut, Menu } from "lucide-react";import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { WaitlistDialog } from "./WaitlistDialog";
import { SHOPIFY_URL } from "../lib/config";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "About Laura", to: "/about" },
  { label: "Books", to: "/books" },
  { label: "Resources", to: "/resources" },
  { label: "Blog", to: "/blog" },
  { label: "Shop", href: SHOPIFY_URL },
  { label: "Podcast", to: null, note: "Coming Soon" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const current = pathname + search;

  const NavLink = ({ l, onClick }) => {
    if (l.href)
      return (
        <a
          href={l.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          className="text-[0.8rem] uppercase tracking-wider transition-colors text-ivory/90 hover:text-gold"
          data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
        >
          {l.label}
        </a>
      );
    if (!l.to)
      return (
        <span className="flex flex-col items-center leading-none text-ivory/40 cursor-default" data-testid="nav-podcast">
          <span className="text-[0.8rem] uppercase tracking-wider">{l.label}</span>
          <span className="text-[0.6rem] text-gold/80 normal-case tracking-wide mt-0.5">{l.note}</span>
        </span>
      );
    const active = current === l.to;
    return (
      <Link
        to={l.to}
        onClick={onClick}
        className={`text-[0.8rem] uppercase tracking-wider transition-colors ${active ? "text-gold" : "text-ivory/90 hover:text-gold"}`}
        data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-forest border-b border-gold/25" data-testid="main-navbar">
      <nav className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <span className="bg-ivory rounded-xl p-1.5 flex items-center justify-center shadow-sm">
            <img src="/brand/logo-icon.webp" alt="Laura Combs Productions" className="h-[3.25rem] w-auto" width="76" height="53" />
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-xl xl:text-2xl text-ivory tracking-wide">LAURA COMBS</span>
            <span className="text-[0.66rem] tracking-[0.32em] text-gold">PRODUCTIONS</span>
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-6">
          {LINKS.map((l) => <NavLink key={l.label} l={l} />)}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" aria-label="Open account menu" className="rounded-full gap-2 text-ivory hover:bg-ivory/10 hover:text-ivory" data-testid="user-menu-trigger">
                  <span className="w-8 h-8 rounded-full bg-gold text-forest flex items-center justify-center text-sm font-medium">
                    {user.name?.[0]?.toUpperCase()}
                  </span>
                  <span className="hidden sm:inline text-sm">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white border-gold/40">
                <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                  <User className="w-4 h-4 mr-2" /> My Account
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <WaitlistDialog source="navbar">
            <Button
              className="rounded-full bg-transparent border border-gold text-gold hover:bg-gold hover:text-forest px-5 gap-2 transition-colors hidden sm:flex"
              data-testid="join-community-btn"
            >
              <Mail className="w-4 h-4" /> Join the Community
            </Button>
          </WaitlistDialog>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open navigation menu" className="xl:hidden text-ivory hover:bg-ivory/10" data-testid="mobile-menu-btn">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-forest border-gold/25 w-72">
              <div className="flex flex-col gap-6 mt-10">
                {LINKS.map((l) => <NavLink key={l.label} l={l} onClick={() => setOpen(false)} />)}
                <WaitlistDialog source="mobile-menu">
                  <Button className="rounded-full bg-gold hover:bg-gold/90 text-forest gap-2 mt-4 transition-colors" data-testid="mobile-join-btn">
                    <Mail className="w-4 h-4" /> Join the Community
                  </Button>
                </WaitlistDialog>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
